/// <amd-module name="Core/Medius.Core.Web/Scripts/components/taskHandling/shared/modals/rejectManagementWizard/emailAttachments/emailFileAttachment"/>
import * as React from 'react';
import { FileItem, FileUploader, FlexLayout, SelectionErrorResponse, Text, UploaderErrorCode, UploadProgressEnum, Notification, NotificationFeedbackTypeEnum, StandardMaxFileSizeEnum, ErrorInfo } from '@medius/ui-controls';
import { getFormattedLabelTranslation, translate } from 'Core/Medius.Core.Web/Scripts/lib/globalization';
import { toAction } from "Core/Medius.Core.Web/Scripts/Medius/lib/path";
import { ajax } from "Core/Medius.Core.Web/Scripts/Medius/lib/ajax";
import { IAttachmentItem, supportedExtensions } from './interfaces';
import { InvoiceImageItem } from './invoiceImageItem';
import { UseFormSetValue, FieldValues } from 'react-hook-form';

const DEFAULT_MAX_FILE_SIZE = 3 * StandardMaxFileSizeEnum.TenMB;
const MAX_ATTACHMENTS: number | null = null;

// the same as for uploading attachment on the invoice
async function getAttachmentMaxSize(): Promise<number> {
    const result = new Promise<number>((resolve) => {
        ajax(toAction("GetAttachmentMaxSize", "~/Attachments"), undefined)
        .done((maxSize: any) => resolve(maxSize));
    });

    return result;
}

type RejectManagementEmailContext = {
    setValue: UseFormSetValue<FieldValues>;
};

type EmailFileAttachmentProps = {
    documentType: string;
    documentId: number;
    formContext: RejectManagementEmailContext;
};

export const EmailFileAttachment = ({documentType, documentId, formContext}: EmailFileAttachmentProps) => {
    const [attachments, setAttachments] = React.useState<IAttachmentItem[]>([]);
    
    React.useEffect(() => {
        const { setValue } = formContext;
        setValue("attachments", attachments);
    }, [attachments, formContext]);

    const [maxAttachmentSize, setMaxAttachmentSize] = React.useState<number>(DEFAULT_MAX_FILE_SIZE);
    const hasAnyFiles = attachments.length > 0;
    const [reset, setReset] = React.useState(false);
    const [hasTooManyFiles, setHasTooManyFiles] = React.useState(false);
    
    const maxFilesLeft: number | null = React.useMemo(() => {
        if (typeof MAX_ATTACHMENTS === "number") {
            const validAttachments = attachments.filter(att => att.status !== UploadProgressEnum.Failed);
            return Math.max(MAX_ATTACHMENTS - validAttachments.length, 0);
        }
        return null;
    }, [attachments]);

    React.useEffect(() => {
        const controller = new AbortController();

        getAttachmentMaxSize()
            .then((size) => { 
                if (!controller.signal.aborted)
                    setMaxAttachmentSize(size);
            });
        
        return () => {
            controller.abort();
        };
    }, []);

    const isDuplicate = (file: File) =>
        attachments.some(
            (a) =>
                a.file.name === file.name &&
                a.file.size === file.size &&
                a.file.lastModified === file.lastModified
        );

    const makeId = (file: File) => {
        const safeName = file.name.replace(/\s+/g, "_");
        return (globalThis.crypto?.randomUUID?.() ??
            `${safeName}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`);
    };

    const mapErrorCodeToMessage = (code: UploaderErrorCode) => {
        const fileInvalidTypeError = 
            translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/invalidFileFormat");
        const fileTooLargeError = 
            getFormattedLabelTranslation("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/fileTooLarge", 
                [`${maxAttachmentSize / StandardMaxFileSizeEnum.OneMB} MB`]);
        const fileGeneralError = 
            translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/genericFileUploadError");
        const tooManyFilesError = 
            getFormattedLabelTranslation("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/tooManyFilesError", 
                [typeof MAX_ATTACHMENTS === "number" ? String(MAX_ATTACHMENTS) : ""]);

        switch (code) {
            case "fileInvalidType":
                return fileInvalidTypeError;
            case "fileTooLarge":
                return fileTooLargeError;
            case "tooManyFiles":
                return tooManyFilesError;
            default:
                return fileGeneralError;
        }
    };

    const handleAccepted = (files: File[]) => {
        setHasTooManyFiles(false);
        if (!files?.length) return;

        setAttachments((prev) => {
            const next: IAttachmentItem[] = [...prev];

            for (const file of files) {
                if (isDuplicate(file)) {
                    continue;
                }

                next.push({
                    id: makeId(file),
                    file,
                    status: UploadProgressEnum.Uploaded,
                    error: undefined,
                    addedAtTimestamp: Date.now(),
                });
            }

            next.sort((a, b) => b.addedAtTimestamp - a.addedAtTimestamp);
            return next;
        });
    };

    
    const handleRejected = (response: SelectionErrorResponse) => {
        setHasTooManyFiles(false);
        if (!response?.fileErrors?.length && !response?.generalErrors?.length) return;
        
        const tooManyFiles = response?.generalErrors?.some(
            ge => ge.code === "tooManyFiles"
        ) || 
        response?.fileErrors?.some(fe => fe.errors?.some(e => e.code === "tooManyFiles"));
        
        if (tooManyFiles) {
            setHasTooManyFiles(true);
            setReset(prev => !prev);           
            return;
        }

        setAttachments((prev) => {
            const next: IAttachmentItem[] = [...prev];

            for (const fe of response.fileErrors) {
                const file = fe.file;
                const code = (fe.errors?.[0]?.code ?? "unknown") as UploaderErrorCode;

                if (isDuplicate(file)) continue;

                if (typeof MAX_ATTACHMENTS === "number" && next.length >= MAX_ATTACHMENTS) {
                    break;
                }

                next.push({
                    id: makeId(file),
                    file,
                    status: UploadProgressEnum.Failed,
                    error: mapErrorCodeToMessage(code),
                    addedAtTimestamp: Date.now(),
                });
            }

            next.sort((a, b) => b.addedAtTimestamp - a.addedAtTimestamp);
            setReset(prev => !prev);
            return next;
        });
    };

    const handleDelete = (id: string) => {
        setAttachments((prev) => prev.filter((a) => a.id !== id));
    };

    const fileValidation = (): ErrorInfo => {
        if(maxFilesLeft === 0)
            return {message: "tooManyFiles", code: "tooManyFiles"};
    };

    return (
        <FlexLayout direction="column" itemRowSpacing="small" width="fill" horizontalAlignment="fill">
            <Text variant="heading3">{translate("#Core/attachments")}</Text>
            <InvoiceImageItem 
                documentId={documentId} 
                documentType={documentType} />
            {hasAnyFiles &&
                attachments.map((item) => (
                    <FileItem
                        key={item.id}
                        name={item.file.name}
                        size={item.file.size}
                        progressStatus={item.status}
                        errorMessage={item.error}
                        getProgressPercentage={undefined}
                        onDelete={() => handleDelete(item.id)}
                        onCancel={() => handleDelete(item.id)}
                    />
            ))}

            <FileUploader
                key={`uploader-key-${reset ? "0": "1"}`}
                isDropZone
                label={translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/uploadFiles")}
                onAccepted={handleAccepted}
                onRejected={handleRejected}
                maxFiles={maxFilesLeft}
                maxSize={maxAttachmentSize}
                acceptedTypes={supportedExtensions}
                customFileValidation={fileValidation}
            />
            {
                hasTooManyFiles &&
                <Notification
                    feedbackType={NotificationFeedbackTypeEnum.Warning}
                    text={mapErrorCodeToMessage("tooManyFiles")}
                    onRequestClose={() => setHasTooManyFiles(false)}
                /> 
            }
        </FlexLayout>
    );
};